/* prettier-ignore */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild, computed, inject, signal, Renderer2, OnDestroy, effect } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { PulseIconComponent } from 'src/chatbot/icons/send-icon/pulse-icon.component';
import { VoiceIconComponent } from 'src/chatbot/icons/send-icon/voice-icon.component';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { RecordAudioHandlerService } from 'src/chatbot/service/recordAudioHandler.service';
import { MyHammerConfig } from 'src/app/config/hammer.config';
import * as Hammer from 'hammerjs';
import { Subscription } from 'rxjs';

@Component({
  selector: 'voice-button',
  standalone: true,
  imports: [CommonModule, VoiceIconComponent, PulseIconComponent],
  providers: [{ provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig }],
  templateUrl: './voiceButton.component.html',
  styleUrl: './voiceButton.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VoiceButtonComponent implements OnDestroy {
  @ViewChild('dynamicContainer', { static: true }) dynamicContainer: ElementRef<HTMLButtonElement>;
  @Output() public actionEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Input() chatForm!: FormGroup;

  private recordService = inject(RecordAudioHandlerService);
  private dataChat = inject(DataChatService);
  private renderer = inject(Renderer2);

  public showRecordIcon = computed(() => this.recordService.showRecordIcon());
  public isRecording = computed(() => this.recordService.isRecording());
  public chatIsTyping = computed(() => this.dataChat.chatIsTyping());
  public isStreaming = computed(() => this.dataChat.isStreaming());
  public isInvalid = signal<boolean>(true);

  private hammer: HammerManager | undefined;
  private pressed = false; // evita duplicados
  private removeListeners: Array<() => void> = [];
  private formSub?: Subscription;

  enableHammer = effect(() => {
    const hideChat = this.dataChat.hideChat();
    if (hideChat) {
      this.destroyHammer();
      return;
    }

    this.initHammer();
  })

  ngOnInit() {
    this.formSub = this.chatForm.valueChanges.subscribe(() => {
      this.isInvalid.set(this.chatForm.invalid);
    });
  }

  initHammer() {
    const el = this.dynamicContainer?.nativeElement;
    if (!el) return;

    this.hammer = new Hammer(el);
    // Solo usamos 'press' para iniciar
    this.hammer.on('press', (ev) => {
      this.dataChat.hasHistory.set(true);
      // Evita que iOS intente abrir menú/contexto
      try { (ev.srcEvent as Event)?.preventDefault?.(); } catch { }
      this.onLongPress();
    });

    // En iOS 'pressup' es poco fiable -> lo omitimos y añadimos respaldos nativos
    this.addEndListeners(el);
  }

  private addEndListeners(el: HTMLElement) {
    // Listeners que marcan el fin del gesto (cubre iOS/Android/Desktop)
    const end = () => this.onLongPressEnd();

    this.removeListeners.push(
      this.renderer.listen(el, 'touchend', end),
      this.renderer.listen(el, 'touchcancel', end),
      this.renderer.listen(el, 'pointerup', end),
      this.renderer.listen(el, 'pointercancel', end),
      this.renderer.listen(el, 'mouseup', end),
      this.renderer.listen(el, 'mouseleave', end),
      // Evita el menú de contexto de iOS al mantener pulsado
      this.renderer.listen(el, 'contextmenu', (e: Event) => e.preventDefault())
    );
  }

  onLongPress() {
    // return;
    if (this.pressed) return; // ya iniciamos
    this.pressed = true;
    console.log("LONGPRESS");
    this.recordService.showRecordIcon.set(true);
    this.actionEmitter.emit('START');
  }

  onLongPressEnd() {
    // return;
    if (!this.pressed) return; // ignora si no hubo press previo
    this.pressed = false;
    console.log("LONGPRESSEND");
    this.recordService.showRecordIcon.set(false);
    this.actionEmitter.emit('STOP');
  }

  click() {
    // Toggle: click para iniciar, click para parar
    if (this.isRecording()) {
      // Ya está grabando, parar
      console.log("CLICK -> STOP");
      this.recordService.showRecordIcon.set(false);
      this.actionEmitter.emit('STOP');
    } else {
      // No está grabando, iniciar
      console.log("CLICK -> START");
      this.dataChat.hasHistory.set(true);
      this.recordService.showRecordIcon.set(true);
      this.actionEmitter.emit('START');
    }
  }

  destroyHammer() {
    if (this.hammer) {
      this.hammer.off('press');
      this.hammer.destroy?.(); // si tu versión de Hammer lo soporta
      this.hammer = undefined;
    }
    this.removeListeners.forEach((off) => {
      try { off(); } catch { }
    });
    this.removeListeners = [];
    this.formSub?.unsubscribe();
  }

  ngOnDestroy() {
    this.destroyHammer();
  }
}